import { LOGOUT } from '@app/actions/user';
import {
  ADD_OR_UPDATE_TICKETS,
  START_PROCESSING_ORDER,
  PROCESSING_ORDER_ERROR,
  ORDER_ASSIGNMENT_LOADING,
  ORDER_ASSIGNMENT_DONE,
  SET_CURRENT_ORDER_ID,
  UPDATE_ORDER_QUEUE,
} from '@app/actions/orders';
import getFilteredDealerOrders from '@app/selectors/get-filtered-dealer-orders';
import importantOnTop from '@app/util/orders-sort';
import {
  QUEUE_INCOMING,
  QUEUE_BEING_DELIVERED,
  TYPE_CANCEL_NEW_ORDER,
  TYPE_CANCEL_DELIVER_ORDER,
  ORDER_STATUS_CONSUMER_CANCELLED,
} from '@chama/utils/constants/status';
import { getIncomingOrdersDropdownValue, getOngoingOrdersDropdownValue } from '@app/reducer/form';
import { getCurrentTime } from '@app/reducer/time';
import dealerSelectors from '@app/selectors/dealerSelectors';
import orderTimeCount from '@chama/utils/functions/order-time-count';

import updateBeingProcessedOrderIds from './update-processing-order-ids';

const ticketToOrder = ({ data: { order, delivery }, ...ticket }) => ({
  ...order,
  orderId: order.id,
  delivery,
  currentTicket: ticket,
});

/**
 * This method here contains a big Redux anti-pattern which is mutability
 * it mutate the state when it is executed, making only possible to see the previous state
 * when everything is executed before the execution of this one.
 * Angry note: By the lack of time, I can't fix this now (time of writing) and
 * the developer who wrote this (Bas) should really read about good practices.
 * I literally lost more than 1 hour trying to find a bug in a perfectly fine code
 * when the problem was this stupid method. Yeah, other ppl's code can sometimes be a nightmare
 * indeed.
 *
 * Be aware of this!
 */
const updateStateWithOrders = (state, updatedOrders) => {
  return updatedOrders.reduce((prevState, order) => {
    // only update order if the ticket is valid to be displayed (has a queue property)
    if (!(order.id in prevState) || order.currentTicket.queue) {
      // eslint-disable-next-line no-param-reassign
      prevState[order.id] = order;
    }

    return prevState;
  }, state);
};

const ordersToDict = orders =>
  orders.reduce((memo, order) => {
    return {
      [order.id]: order,
      ...memo,
    };
  }, {});

export const ORDERS_QUEUES = Object.freeze({
  INCOMING: 'incoming',
  BEING_DELIVERED: 'beingDelivered',
  FINALIZED: 'finalized',
});

const initialState = {
  selectedOrderQueue: ORDERS_QUEUES.INCOMING,
  isExecutingCommand: false,
  isExecutingAssignment: false,
  fetched: false,
  currentOrderId: null,
  beingProcessedOrderIds: [],
  dict: {},
};

const orders = (state = initialState, action) => {
  let updatedOrders;

  switch (action.type) {
    case ADD_OR_UPDATE_TICKETS:
      updatedOrders = action.tickets.map(ticketToOrder);

      return {
        ...state,
        fetched: true,
        beingProcessedOrderIds: updateBeingProcessedOrderIds(
          state.dict,
          state.beingProcessedOrderIds,
          updatedOrders,
        ),
        dict: ordersToDict(updatedOrders),
      };

    case START_PROCESSING_ORDER:
      return {
        ...state,
        beingProcessedOrderIds: [...state.beingProcessedOrderIds, action.orderId],
      };

    case PROCESSING_ORDER_ERROR:
      return {
        ...state,
        beingProcessedOrderIds: state.beingProcessedOrderIds.filter(
          orderId => orderId !== action.orderId,
        ),
      };

    case SET_CURRENT_ORDER_ID:
      return {
        ...state,
        currentOrderId: action.currentOrderId,
      };

    case ORDER_ASSIGNMENT_LOADING:
      return {
        ...state,
        isExecutingAssignment: true,
      };

    case ORDER_ASSIGNMENT_DONE:
      return {
        ...state,
        isExecutingAssignment: false,
      };

    case UPDATE_ORDER_QUEUE: {
      return {
        ...state,
        selectedOrderQueue: action.payload.queue,
        fetched: false,
        currentOrderId: null,
        beingProcessedOrderIds: [],
      };
    }

    case LOGOUT:
      return {
        ...initialState,
      };

    default:
      return state;
  }
};

const getNewOrders = state =>
  getFilteredDealerOrders(state).filter(
    ({ currentTicket }) => currentTicket.queue === QUEUE_INCOMING,
  );

const getOngoingOrders = state =>
  getFilteredDealerOrders(state).filter(
    ({ currentTicket }) => currentTicket.queue === QUEUE_BEING_DELIVERED,
  );

const getFilteredNewOrders = state => {
  const ordersNew = getNewOrders(state);
  const visibilityFilter = getIncomingOrdersDropdownValue(state);
  const currentTime = getCurrentTime(state);
  const dealers = dealerSelectors.getDealers(state);

  const laterThanTwoMinutesOrStoreIsInactive = myOrder => {
    const dealer = dealers[myOrder.dealerId];
    return (
      (dealer && !dealer.isActive) || orderTimeCount(currentTime, myOrder.creationTime) > 2 * 60
    );
  };

  switch (visibilityFilter) {
    case 'SHOW_ALL':
      return ordersNew.sort(importantOnTop(TYPE_CANCEL_NEW_ORDER));
    case 'SHOW_AWAITING_INTERACTION':
      return ordersNew.filter(isNotCanceled).filter(laterThanTwoMinutesOrStoreIsInactive);
    default:
      return ordersNew;
  }
};

const getFilteredOngoingOrders = state => {
  const ongoingOrders = getOngoingOrders(state);
  const visibilityFilter = getOngoingOrdersDropdownValue(state);

  const lateOrderFilter = myOrder => {
    const currentTime = getCurrentTime(state);
    const secs = orderTimeCount(currentTime, myOrder.eta, true);

    if (secs < 0) return true;
    return false;
  };

  switch (visibilityFilter) {
    case 'SHOW_ALL':
      return ongoingOrders.sort(importantOnTop(TYPE_CANCEL_DELIVER_ORDER));
    case 'SHOW_LATE_ORDERS':
      return ongoingOrders.filter(lateOrderFilter).sort(importantOnTop());
    default:
      return ongoingOrders;
  }
};

const isNotCanceled = myOrder => myOrder.status !== ORDER_STATUS_CONSUMER_CANCELLED;

export default orders;
export { getFilteredNewOrders, getFilteredOngoingOrders, getNewOrders, getOngoingOrders };



// WEBPACK FOOTER //
// ./src/reducer/orders.js